import { FC, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Paper } from '@mui/material';
import {
  TransferOutcome,
  VsTransferEvent,
  chatActions,
  useAppDispatch,
} from '@vs-online-only-app/state';
import { useSelectedHeroConversation } from '../../../hooks/useSelectedHeroConversation';
import { PendingTransferCard } from './PendingTransferCard';
import { AcceptedTransferCard } from './AcceptedTransferCard';
import { ExpiredTransferCard } from './ExpiredTransferCard';

type RootTransferCardProps = {
  $slideIn?: boolean;
};

export const RootTransferCard = styled(Paper)<RootTransferCardProps>`
  background: ${({ theme }) => theme.palette.transferNotification.main};
  padding: ${({ theme }) => theme.spacing(2)};
  gap: ${({ theme }) => theme.spacing(2)};
  border: 2px solid ${({ theme }) => theme.palette.common.white};
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2}px;
  width: 400px;
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;

  ${({ $slideIn }) =>
    $slideIn &&
    css`
      animation: slide-in 0.2s ease-out;

      @keyframes slide-in {
        from {
          transform: translateY(20px);
        }
        to {
          transform: translateY(0);
        }
      }
    `}
`;

type Props = {
  transferRequest: VsTransferEvent;
  onClose: () => void;
  onCancel: () => void;
  slideIn?: boolean;
};

export const OutgoingTransferCard: FC<Props> = ({
  transferRequest,
  onClose,
  onCancel,
  slideIn,
}) => {
  const dispatch = useAppDispatch();
  const selectedConversation = useSelectedHeroConversation();

  useEffect(() => {
    /**
     * if the transfer was accepted by another associate and we are still viewing the conversation,
     * we deselect it as the associate is not part of it any more
     */
    if (
      transferRequest.outcome === TransferOutcome.ACCEPTED &&
      selectedConversation?.id === transferRequest.conversation
    ) {
      dispatch(chatActions.setSelectedConversation(undefined));
    }
  }, [transferRequest.outcome]);

  if (!transferRequest) {
    return null;
  }

  if (transferRequest.open) {
    return (
      <PendingTransferCard
        event={transferRequest}
        onCancel={onCancel}
        slideIn={slideIn}
      />
    );
  }

  if (transferRequest.outcome === TransferOutcome.ACCEPTED) {
    return (
      <AcceptedTransferCard
        event={transferRequest}
        onClose={onClose}
        slideIn={slideIn}
      />
    );
  }

  if (
    transferRequest.outcome === TransferOutcome.EXPIRED ||
    (transferRequest.outcome === TransferOutcome.DECLINED &&
      !transferRequest.open)
  ) {
    return (
      <ExpiredTransferCard
        event={transferRequest}
        onClose={onClose}
        slideIn={slideIn}
      />
    );
  }

  return null;
};
